import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, lighten, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import { makeStyles } from "helpers/StyleHelper";
import React, { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import type { ExecuteMultipleAction } from "./index";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    diplay: "flex",
    justifyContent: "space-between",
  },
  highlight:
    theme.palette.mode === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

type EnhancedTableToolbarProps = {
  title?: string;
  titleComponent?: React.ReactNode;
  selectedIds?: string[];
  onEdit?: (id: string) => void;
  onDelete?: (ids: string[]) => void;
  executeMultiple?: ExecuteMultipleAction[];
  numSelectedMessageId?: string;
};

const EnhancedTableToolbar = ({
  title,
  titleComponent,
  selectedIds = [],
  onEdit,
  onDelete,
  executeMultiple = [],
  numSelectedMessageId,
}: EnhancedTableToolbarProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [actionToConfirm, setActionToConfirm] =
    useState<ExecuteMultipleAction | null>(null);

  // The INDEX of the action running, not the action: ExpandableTable rebuilds its wrapper objects every
  // render, so identity comparison would silently stop matching. -1 is "none".
  const [runningActionIndex, setRunningActionIndex] = useState(-1);

  const numSelected = selectedIds.length;
  const handleEdit = () => onEdit!(selectedIds[0]);
  const handleDelete = () => onDelete!(selectedIds);
  const handleExecuteMultiple = async () => {
    await actionToConfirm!.onExecuteMultiple(selectedIds);
    setActionToConfirm(null);
  };

  // The confirmed path lets ConfirmDialog catch the rejection and render it in an Alert. An action that runs
  // straight away has no dialog to do that, so the rejection is handed to the action's own onError; with no
  // onError it is rethrown, which surfaces it as an unhandled rejection rather than discarding it.
  //
  // Awaited, and the button is disabled while it runs: an unconfirmed action gives the operator no dialog to
  // pause on, so without this a second click during a slow one ran it twice - two downloads of the same
  // archive, or two of whatever else a caller wires up here.
  const executeWithoutConfirmation = async (
    action: ExecuteMultipleAction,
    index: number
  ) => {
    setRunningActionIndex(index);

    try {
      await action.onExecuteMultiple(selectedIds);
    } catch (error: unknown) {
      if (!action.onError) {
        throw error;
      }

      action.onError(error);
    } finally {
      setRunningActionIndex(-1);
    }
  };

  const toolbarStyles = {
    ...styles.root,
    ...(numSelected > 0 ? styles.highlight : {}),
  };

  return (
    <>
      <Toolbar sx={toolbarStyles}>
        {numSelected > 0 ? (
          <Typography
            sx={styles.title}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            <T
              id={numSelectedMessageId ?? "tableNumSelected"}
              values={{ numSelected }}
            />
          </Typography>
        ) : (
          (title && (
            <Typography
              sx={styles.title}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              {title}
            </Typography>
          )) ||
          (titleComponent && (
            <Box sx={styles.title}>{titleComponent}</Box>
          ))
        )}

        {onEdit && numSelected === 1 && (
          <Tooltip onClick={handleEdit} title={t({ id: "tableEditTooltip" })}>
            <IconButton aria-label={t({ id: "tableEditTooltip" })}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}

        {onDelete &&
          numSelected > 0 &&
          !executeMultiple.some((action) => !!action.buttonLabel) && (
            <Tooltip
              onClick={handleDelete}
              title={t({ id: "tableDeleteTooltip" }, { num: numSelected })}
            >
              <IconButton
                aria-label={t(
                  { id: "tableDeleteTooltip" },
                  { num: numSelected }
                )}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}

        {executeMultiple
          .filter((action) => !!action.buttonLabel)
          .map((action, index) => (
            <Button
              // Index rather than the label alone: two actions may share a label, and React
              // would then treat them as one node.
              key={`${index}-${action.buttonLabel}`}
              size="small"
              variant="outlined"
              // Without this, a disabled button reads as "select a row" - by far the common cause - while
              // something is actually running. An action that never settles would otherwise leave every
              // bulk button dead with nothing on screen to say why.
              startIcon={
                runningActionIndex === index ? (
                  <CircularProgress size={14} />
                ) : undefined
              }
              // A selection the action cannot act on is refused here rather than on click: offering the
              // button and then reporting that nothing was queued reads as a failure, when the screen
              // already knew none of those rows were eligible.
              disabled={
                numSelected === 0 ||
                runningActionIndex !== -1 ||
                action.isEnabledFor?.(selectedIds) === false
              }
              sx={{
                whiteSpace: "nowrap",
                // The title claims a 100% flex basis, so without this the buttons are compressed below
                // their label width and the text overflows the border. One button left enough slack to
                // hide it; two do not.
                flexShrink: 0,
                borderRadius: 2,
                ...(index > 0 && { marginLeft: 1 }),
              }}
              onClick={() => {
                if (action.dialogLabel) {
                  setActionToConfirm(action);
                  return;
                }

                executeWithoutConfirmation(action, index);
              }}
            >
              {action.buttonLabel}
            </Button>
          ))}
      </Toolbar>
      <ConfirmDialog
        dialogTitleKey="Confirm"
        contentKey={actionToConfirm?.dialogLabel}
        open={!!actionToConfirm}
        onCancel={() => setActionToConfirm(null)}
        handleConfirm={handleExecuteMultiple}
      />
    </>
  );
};

export default EnhancedTableToolbar;
